/* eslint-disable react/no-array-index-key */
import FormControl from "common/components/FormControl/FormControl";
import FormScrollError from "common/components/FormScrollError/FormScrollError";
import { useMemo } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import arrayMutators from "final-form-arrays";
import useAuth from "common/hooks/useAuth";
import EditHeader from "common/components/EditHeader/EditHeader";
import usePark from "common/hooks/usePark";
import dayjs from "dayjs";
import EnclosureSelect from "components/select/EnclosureSelect/EnclosureSelect";
import Button from "common/components/Button/Button";
import exportData from "common/utils/exportData";

function QuestionItem({ question, sectionIndex, questionIndex }: any) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col border-[1px] border-primary-500/10 rounded-lg p-4">
      <FormControl
        name={`sections[${sectionIndex}].questions[${questionIndex}].answer`}
        editable={false}
        title={
          question.key ? `${question.key}. ${question.title}` : question.title
        }
        type="select-list"
        containerStyle="w-full"
        options={[
          { value: "yes", label: t("observation.yes") },
          { value: "a_bit", label: t("observation.a_bit") },
          { value: "not_so_much", label: t("observation.not_so_much") },
          { value: "no", label: t("observation.no") },
          { value: "dont_know", label: t("observation.dont_know") },
          { value: "no_answer", label: t("observation.no_answer") },
        ]}
      />
      {question.comment && (
        <FormControl
          name={`sections[${sectionIndex}].questions[${questionIndex}].comment`}
          title={t("forms.comment")}
          containerStyle="flex w-full mt-4"
          editable={false}
          type="textarea"
          className="text-input"
        />
      )}
    </div>
  );
}

function SectionItem({ section, sectionIndex }: any) {
  return (
    <div key={section.id} className="flex flex-col my-4">
      <span className="text-2xl font-bold text-black">{section.title}</span>
      <div className="flex flex-col gap-8 mt-4">
        {section?.questions?.map((question: any, questionIndex: number) => (
          <QuestionItem
            question={question}
            key={question?.id}
            sectionIndex={sectionIndex}
            questionIndex={questionIndex}
          />
        ))}
      </div>
    </div>
  );
}

function ObservationResponseForm({ observationResponse }: any) {
  const { t } = useTranslation();
  const { park: currentPark } = usePark();
  const { user: currentUser } = useAuth();

  const initialValues = useMemo(
    () =>
      observationResponse?.id
        ? {
            ...observationResponse,
            enclosure: observationResponse.enclosure?.id,
            park: observationResponse.park?.id ?? currentPark?.id,
          }
        : {
            park: currentPark?.id,
          },
    [currentPark?.id, observationResponse]
  );

  return (
    <Form
      onSubmit={() => {}}
      mutators={{
        ...arrayMutators,
        setFieldValue: ([field]: any, state: any, utils: any) => {
          utils.changeValue(state, field.field, () => field.value);
        },
      }}
      subscription={{ values: true, submitting: true, pristine: true }}
      initialValues={initialValues}
      render={({ handleSubmit, values }) => {
        return (
          <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
            {/* Header */}
            <EditHeader
              title={t("titlePages.observationResponseDetails")}
              subtitle={values.name}
              icon="ClipboardDocumentCheckIcon"
              HeaderButtonsEdit={
                <Button
                  title={t("actions.export")}
                  type="neutral-line"
                  icon="AiOutlineDownload"
                  onClick={() =>
                    exportData({
                      importType: "observationGrid",
                      id: observationResponse?.id,
                      lang: currentUser?.language,
                    })
                  }
                />
              }
            />
            <form onSubmit={handleSubmit} noValidate>
              <FormScrollError />

              <div className="responsive-flex gap-2 mt-2">
                <div className="md:w-full mb-2 md:mb-0">
                  <div className="flex flex-col gap-2 w-full">
                    <div className="white-box flex items-center justify-center">
                      <div className="responsive-flex gap-4 w-1/2">
                        <div className="flex flex-col w-full gap-4">
                          <div className="flex flex-col">
                            <span className="font-bold text-2xl text-black">
                              {t("observation.observationOf")}{" "}
                              <span className="text-green-700">
                                <span>
                                  {observationResponse.animalName ??
                                    observationResponse.animal?.houseName ??
                                    observationResponse.animalLocalId ??
                                    observationResponse.animal?.localId ??
                                    `Groupe ${
                                      observationResponse.observationGroupName ??
                                      observationResponse.observationGroup?.name
                                    }`}
                                </span>{" "}
                                {Boolean(
                                  observationResponse.animalSpeciesScientificName ??
                                    observationResponse.animal?.species
                                      ?.scientificName ??
                                    observationResponse.observationGroupSpeciesScientificName ??
                                    observationResponse.observationGroup
                                      ?.animals?.[0]?.species?.scientificName
                                ) && (
                                  <span className="italic">
                                    (
                                    {observationResponse.animalSpeciesScientificName ??
                                      observationResponse.animal?.species
                                        ?.scientificName ??
                                      observationResponse.observationGroupSpeciesScientificName ??
                                      observationResponse.observationGroup
                                        ?.animals?.[0]?.species?.scientificName}
                                    )
                                  </span>
                                )}
                              </span>
                            </span>
                            {observationResponse?.observationGrid?.name && (
                              <span>
                                {observationResponse.observationGrid?.name}
                              </span>
                            )}
                          </div>
                          <span className="font-bold text-black text-lg">
                            {t("observation.observationConditions")}
                          </span>
                          <div className="flex flex-row gap-2">
                            <FormControl
                              value={
                                dayjs(
                                  observationResponse?.observationDateTime
                                ).format("L") as any
                              }
                              name="observationDateTime"
                              title={t("forms.date")}
                              containerStyle="flex w-full"
                              className="text-input"
                              editable={false}
                            />
                            <FormControl
                              value={
                                dayjs(
                                  observationResponse?.observationDateTime
                                ).format("HH:mm") as any
                              }
                              name="observationDateTime"
                              title={t("forms.hour")}
                              containerStyle="flex w-full"
                              className="text-input"
                              editable={false}
                            />
                          </div>
                          <FormControl
                            type="select-list"
                            name="weather"
                            editable={false}
                            multiple
                            title={t("observation.weather")}
                            options={[
                              {
                                value: "sunny",
                                label: t("observation.sunny"),
                              },
                              {
                                value: "cloudy",
                                label: t("observation.cloudy"),
                              },
                              {
                                value: "rainy",
                                label: t("observation.rainy"),
                              },
                              {
                                value: "windy",
                                label: t("observation.windy"),
                              },
                              {
                                value: "snowy",
                                label: t("observation.snowy"),
                              },
                            ]}
                            containerStyle="flex w-full"
                          />
                          <FormControl
                            name="observationConditions"
                            title={t(
                              "observation.observationConditionsQuestion"
                            )}
                            containerStyle="flex w-full"
                            editable={false}
                            type="textarea"
                            className="text-input"
                          />
                          <FormControl
                            title={t("observation.userPosition")}
                            name="userPosition"
                            type="select-list"
                            multiple
                            editable={false}
                            options={[
                              {
                                value: "out_of_enclosure",
                                label: t("observation.out_of_enclosure"),
                              },
                              {
                                value: "in_enclosure",
                                label: t("observation.in_enclosure"),
                              },
                              {
                                value: "walking",
                                label: t("observation.walking"),
                              },
                              {
                                value: "by_car",
                                label: t("observation.by_car"),
                              },
                              { value: "still", label: t("observation.still") },
                            ]}
                            containerStyle="flex w-full"
                          />
                          <FormControl
                            title={t("observation.isUserVisibleByTargetAnimal")}
                            name="isUserVisibleByTargetAnimal"
                            type="select-list"
                            editable={false}
                            options={[
                              {
                                value: "yes",
                                label: t("observation.yes"),
                              },
                              {
                                value: "no",
                                label: t("observation.no"),
                              },
                            ]}
                            containerStyle="flex w-full"
                          />
                          <FormControl
                            title={t("observation.isTargetAnimalInEnclosure")}
                            name="isTargetAnimalInEnclosure"
                            type="select-list"
                            editable={false}
                            options={[
                              {
                                value: "yes",
                                label: t("observation.yes"),
                              },
                              {
                                value: "no",
                                label: t("observation.no"),
                              },
                            ]}
                            containerStyle="flex w-full"
                          />
                          {observationResponse.isTargetAnimalInEnclosure ===
                          "yes" ? (
                            <FormControl
                              name="enclosure"
                              title={t("forms.enclosure")}
                              type="custom"
                              editable={false}
                            >
                              <EnclosureSelect />
                            </FormControl>
                          ) : (
                            <FormControl
                              name="enclosureDescriptionIfNotInDefaultEnclosure"
                              title={t(
                                "observation.enclosureDescriptionIfNotInDefaultEnclosure"
                              )}
                              containerStyle="flex w-full"
                              type="textarea"
                              editable={false}
                              className="text-input"
                            />
                          )}
                          <FormControl
                            title={t("observation.medicalStatus")}
                            name="medicalStatus"
                            editable={false}
                            containerStyle="flex w-full"
                          />
                          <FormControl
                            title={t("observation.otherSpeciesInEnclosure")}
                            name="otherSpeciesInEnclosure"
                            editable={false}
                            containerStyle="flex w-full"
                          />
                          <FormControl
                            name="comment"
                            title={t("forms.comment")}
                            containerStyle="flex w-full"
                            editable={false}
                            type="textarea"
                            className="text-input"
                          />
                          <div className="w-full h-4 border-b-4 border-secondary-500 border-dashed" />
                          {observationResponse?.sections?.map(
                            (section: any, sectionIndex: number) => (
                              <SectionItem
                                section={section}
                                sectionIndex={sectionIndex}
                                key={section?.id}
                              />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        );
      }}
    />
  );
}
export default ObservationResponseForm;
